import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Grid, Typography, Tooltip, IconButton, Paper } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Filter from 'components/Filter/components';
import FilterMobile from 'components/FilterMobile/components';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';
import Button from 'components/Button';
import Modal from 'components/Modal';
import SelfServiceUserRegistration from 'components/SelfServiceUserRegistration';
import SelfServiceCustomization from 'components/SelfServiceCustomization';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlusCircle, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import { loadSelfServiceUsers, toggleSelfServiceUserStatus } from '../store/thunk';
import { loadEmployee } from 'pages/EmployeesRegistration/store/thunk';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

const headers = [
  { title: 'Nome', field: 'name', sortable: false },
  { title: 'Estabelecimento', field: 'establishmentName', sortable: false }
];

const tableRowWidth = 100;
const pageSize = 10;

export default function SelfService() {
  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const { userId, submit: { isAdminUser } } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { list, total, isLoading } = useSelector(state => state.selfService);

  const isUserAdmin = +isAdminUser == 1;

  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);
  const [toggleStatusRowData, setToggleStatusRowData] = useState(null);

  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / pageSize) || 1;

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadSelfServiceUsers({ userId, establishmentId, page, qp: '' }));
    }
  }, [establishmentId, page]);

  const requestSelfServiceUsers = ({ _, qp }) => {
    dispatch(loadSelfServiceUsers({ userId, establishmentId, page: 1, qp }));
    setPage(1);
  }

  const handleEdit = rowData => {
    dispatch(loadEmployee(userId, rowData?.userId));
    setIsRegistrationModalOpen(true);
  }

  const handleToggleStatus = () => {
    dispatch(toggleSelfServiceUserStatus({ userId, establishmentId, targetUserId: toggleStatusRowData?.userId, page }))
      .then(() => setToggleStatusRowData(null));
  }

  const isTogglingActiveUser = +toggleStatusRowData?.status == 1;

  const actionButton = (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
      <Button
        fullWidth={isMobile}
        type="button"
        color="success"
        startIcon={
          <FontAwesomeIcon
            icon={faPlusCircle}
            color="#FFFFFF"
            size="xs"
          />
        }
        onClick={() => setIsRegistrationModalOpen(true)}
      >
        Adicionar Totem
      </Button>
      <Button
        fullWidth={isMobile}
        type="button"
        color="primary"
        onClick={() => setIsCustomizationModalOpen(true)}
      >
        Personalização
      </Button>
      <Button
        fullWidth={isMobile}
        type="button"
        color="primary"
        onClick={() => history.push('/acompanhamento-veiculos')}
      >
        Acompanhamento de veículos
      </Button>
    </div>
  );

  return(
    <Paper>
      {isRegistrationModalOpen && (
        <SelfServiceUserRegistration
          open={isRegistrationModalOpen}
          onClose={() => setIsRegistrationModalOpen(false)}
          page={page}
        />
      )}
      {isCustomizationModalOpen && (
        <SelfServiceCustomization
          open={isCustomizationModalOpen}
          onClose={() => setIsCustomizationModalOpen(false)}
        />
      )}
      {!!toggleStatusRowData && (
        <Modal
          id="self-service-toggle-status-modal"
          title={isTogglingActiveUser ? 'Desativar Totem' : 'Ativar Totem'}
          maxWidth="sm"
          open={!!toggleStatusRowData}
          onClose={() => setToggleStatusRowData(null)}
        >
          <Typography color="textSecondary" variant="body2">
            Tem certeza que deseja {isTogglingActiveUser ? 'desativar' : 'ativar'} o totem <b>{toggleStatusRowData?.name}</b>?
          </Typography>
          <Grid container style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button
              color="tertiary"
              variant="outlined"
              onClick={() => setToggleStatusRowData(null)}
            >
              Cancelar
            </Button>
            <Button
              color="success"
              loading={isLoading}
              disabled={isLoading}
              onClick={handleToggleStatus}
            >
              Confirmar
            </Button>
          </Grid>
        </Modal>
      )}
      {isMobile ? (
        <FilterMobile
          hasSearchTerm
          handlePageRequest={requestSelfServiceUsers}
          actionButton={actionButton}
        />
      ) : (
        <Filter
          hasSearchTerm
          handlePageRequest={requestSelfServiceUsers}
          actionButton={actionButton}
        />
      )}
      {isMobile ? (
        <TableMobile
          loading={isLoading}
          data={list?.map(item => {
            return {
              ...item,
              nameItem: {
                index: 1,
                label: 'Nome',
                value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{item?.name}</Typography>
              },
              establishmentNameItem: {
                index: 2,
                label: 'Estabelecimento',
                value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{item?.establishmentName}</Typography>
              }
            }
          })}
          actions={[
            {
              action: rowData => (
                <Tooltip placement='top' title='Editar' enterTouchDelay={0}>
                  <IconButton onClick={() => handleEdit(rowData)}>
                    <FontAwesomeIcon
                      icon={faPen}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            },
            isUserAdmin && {
              action: rowData => (
                <Tooltip placement='top' title={+rowData?.status == 0 ? 'Ativar' : 'Desativar'} enterTouchDelay={0}>
                  <IconButton onClick={() => setToggleStatusRowData(rowData)}>
                    <FontAwesomeIcon
                      icon={+rowData?.status == 0 ? faEye : faEyeSlash}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            }
          ].filter(Boolean)}
        />
      ) : (
        <MTable
          loading={isLoading}
          headers={headers}
          data={list}
          actions={[
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faPen}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: 'Editar',
              onClick: () => handleEdit(rowData)
            }),
            isUserAdmin && (rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={+rowData?.status == 0 ? faEye : faEyeSlash}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: +rowData?.status == 0 ? 'Ativar' : 'Desativar',
              onClick: () => setToggleStatusRowData(rowData)
            }))
          ].filter(Boolean)}
          actionColumnWidth={tableRowWidth}
        />
      )}
      <Grid
        container
        xs={12}
        style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
      >
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          count={pageCount}
          page={page}
          onChange={(_, page) => setPage(page)}
        />
      </Grid>
    </Paper>
  );
}